/**
 * #/users — el padrón de la federación, sin curl.
 *
 * Consume los endpoints que YA existen (`apps/api/src/routes/users.ts`):
 *   GET    /api/users              listado (nunca trae el hash)
 *   PUT    /api/users/:username    alta y edición (idempotente)
 *   DELETE /api/users/:username    baja
 *
 * Efectos de seguridad que el API aplica solo y que la pantalla ANUNCIA antes
 * de que el usuario los descubra: cambiar la contraseña o bloquear a alguien
 * revoca sus sesiones al instante, y borrarlo también. "Cerrar sesiones" es un
 * bloqueo + desbloqueo (ver `ApiClient.revokeUserSessions`).
 *
 * El rol lo aplica el API (`users:manage`); acá se degrada ANTES de pedir nada,
 * con el mismo criterio que `pages/match/access.ts`: un 403 no es un error de
 * la app, es el rol, así que se explica en tono neutro y sin botón de reintento.
 */
import { useState } from 'react';
import { Card, Empty, ErrorBox, Loading, Notice } from '../components/States';
import { useT } from '../i18n/use-t';
import { errorMessage, useAsync } from '../hooks';
import {
  ROLES,
  type ApiClient,
  type Identity,
  type PublicUser,
  type Role,
} from '../lib/api-client';
import {
  EMPTY_USER_FORM,
  MIN_PASSWORD_LENGTH,
  USERS_PERMISSION,
  buildUserPayload,
  isSelf,
  roleLabel,
  usersAccess,
  type UserFormValues,
} from '../lib/auth';
import { dateTime } from '../lib/format';

export function UsersPage({
  client,
  identity,
}: {
  client: ApiClient;
  identity: Identity | null;
}): JSX.Element {
  const t = useT();
  const access = usersAccess(identity);
  // El listado sólo se pide si el rol llega: si no, sería un 403 anunciado.
  const users = useAsync(
    () => (access === 'ok' ? client.listUsers() : Promise.resolve([] as PublicUser[])),
    [client, access],
  );
  const [editing, setEditing] = useState<UserFormValues | null>(null);
  /** `null` = alta; con username = edición (el usuario queda bloqueado). */
  const [existing, setExisting] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  if (access === 'anon') {
    return (
      <div>
        <div className="page-head">
          <h1>{t('users.title')}</h1>
        </div>
        <Notice
          message={t('users.anon')}
          action={
            <a className="nav-link" href="#/login">
              {t('auth.login')}
            </a>
          }
        />
      </div>
    );
  }

  if (access === 'forbidden') {
    return (
      <div>
        <div className="page-head">
          <h1>{t('users.title')}</h1>
        </div>
        <Notice
          message={t('users.forbidden')}
          hint={t('users.forbiddenHint', {
            role: identity === null ? '' : roleLabel(identity.role, t),
            permission: USERS_PERMISSION,
          })}
        />
      </div>
    );
  }

  const startNew = (): void => {
    setEditing({ ...EMPTY_USER_FORM });
    setExisting(null);
    setErrors([]);
    setNotice(null);
  };

  const startEdit = (user: PublicUser): void => {
    setEditing({
      username: user.username,
      name: user.name,
      role: user.role,
      password: '',
      disabled: user.disabled,
    });
    setExisting(user.username);
    setErrors([]);
    setNotice(null);
  };

  const save = async (): Promise<void> => {
    if (!editing) return;
    const creating = existing === null;
    const payload = buildUserPayload(editing, creating, t);
    if (!payload.ok) {
      setErrors(payload.errors);
      return;
    }
    setErrors([]);
    setBusy('form');
    try {
      await client.saveUser(payload.username, payload.body);
      setNotice(
        t(creating ? 'users.createdNotice' : 'users.savedNotice', { username: payload.username }),
      );
      setEditing(null);
      setExisting(null);
      users.reload();
    } catch (err) {
      setErrors([errorMessage(err instanceof Error ? err : new Error(String(err)))]);
    } finally {
      setBusy(null);
    }
  };

  /** Acciones de fila: todas reportan el mismo par notice/errors. */
  const run = async (key: string, fn: () => Promise<string>): Promise<void> => {
    setBusy(key);
    setErrors([]);
    try {
      setNotice(await fn());
      users.reload();
    } catch (err) {
      setNotice(null);
      setErrors([errorMessage(err instanceof Error ? err : new Error(String(err)))]);
    } finally {
      setBusy(null);
    }
  };

  const toggleBlock = (user: PublicUser): Promise<void> =>
    run(`block:${user.username}`, async () => {
      await client.saveUser(user.username, {
        name: user.name,
        role: user.role,
        disabled: !user.disabled,
      });
      return t(user.disabled ? 'users.unblockedNotice' : 'users.blockedNotice', {
        username: user.username,
      });
    });

  const revoke = (user: PublicUser): Promise<void> | undefined => {
    if (!window.confirm(t('users.confirmRevoke', { username: user.username }))) return;
    return run(`revoke:${user.username}`, async () => {
      await client.revokeUserSessions(user.username, { name: user.name, role: user.role });
      return t('users.revokedNotice', { username: user.username });
    });
  };

  const remove = (user: PublicUser): Promise<void> | undefined => {
    // Confirmación explícita: la baja cierra sesiones y no se deshace.
    if (!window.confirm(t('users.confirmDelete', { username: user.username }))) return;
    return run(`delete:${user.username}`, async () => {
      await client.deleteUser(user.username);
      if (existing === user.username) {
        setEditing(null);
        setExisting(null);
      }
      return t('users.deletedNotice', { username: user.username });
    });
  };

  const rows = users.data ?? [];

  return (
    <div>
      <div className="page-head">
        <h1>{t('users.title')}</h1>
        <div className="row">
          <button type="button" onClick={users.reload}>
            {t('common.refresh')}
          </button>
          <button type="button" className="primary" onClick={startNew}>
            {t('users.newUser')}
          </button>
        </div>
      </div>

      <p className="hint">{t('users.intro')}</p>

      {notice ? <p className="hint">{notice}</p> : null}
      {users.status === 'error' ? (
        <ErrorBox message={errorMessage(users.error)} onRetry={users.reload} />
      ) : null}
      {errors.length > 0 && editing === null ? (
        <div className="error-box" role="alert">
          <span>{errors[0]}</span>
        </div>
      ) : null}

      {editing ? (
        <Card
          title={existing === null ? t('users.newUser') : t('users.editing', { username: existing })}
        >
          {errors.length > 0 ? (
            <div className="error-box" role="alert">
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {errors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="grid-2">
            <label className="field">
              <span>{t('users.username')}</span>
              <input
                value={editing.username}
                disabled={existing !== null}
                onChange={(e) => setEditing({ ...editing, username: e.target.value })}
                placeholder="nombre.apellido"
                autoComplete="off"
              />
            </label>
            <label className="field">
              <span>{t('users.name')}</span>
              <input
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              />
            </label>
            <label className="field">
              <span>{t('users.role')}</span>
              <select
                value={editing.role}
                onChange={(e) => setEditing({ ...editing, role: e.target.value as Role })}
              >
                {ROLES.map((role) => (
                  <option key={role} value={role}>
                    {roleLabel(role, t)} ({role})
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>
                {existing === null
                  ? t('users.passwordNew', { min: MIN_PASSWORD_LENGTH })
                  : t('users.passwordEdit')}
              </span>
              <input
                type="password"
                value={editing.password}
                onChange={(e) => setEditing({ ...editing, password: e.target.value })}
                autoComplete="new-password"
              />
            </label>
          </div>

          <ul className="hint" style={{ paddingLeft: 18 }}>
            <li>{t('users.roleHintAdmin')}</li>
            <li>{t('users.roleHintScout')}</li>
            <li>{t('users.roleHintCoach')}</li>
            <li>{t('users.roleHintViewer')}</li>
          </ul>

          <label className="field">
            <span>{t('users.disabled')}</span>
            <input
              type="checkbox"
              checked={editing.disabled}
              onChange={(e) => setEditing({ ...editing, disabled: e.target.checked })}
            />
          </label>

          <p className="hint">
            {t('users.passwordHint')} {t('users.disabledHint')}
          </p>

          <div className="row">
            <button
              type="button"
              className="primary"
              disabled={busy === 'form'}
              onClick={() => void save()}
            >
              {busy === 'form' ? t('common.saving') : t('common.save')}
            </button>
            <button type="button" disabled={busy === 'form'} onClick={() => setEditing(null)}>
              {t('common.cancel')}
            </button>
          </div>
        </Card>
      ) : null}

      <Card>
        {users.status === 'loading' && users.first ? (
          <Loading what={t('users.loadingWhat')} />
        ) : null}
        {users.status === 'ok' && rows.length === 0 ? <Empty>{t('users.empty')}</Empty> : null}
        {rows.length > 0 ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('users.colUser')}</th>
                  <th>{t('users.colName')}</th>
                  <th>{t('users.colRole')}</th>
                  <th>{t('users.colStatus')}</th>
                  <th>{t('users.colCreated')}</th>
                  <th>{t('users.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((user) => {
                  const self = isSelf(identity, user);
                  const working = busy !== null && busy.endsWith(`:${user.username}`);
                  return (
                    <tr key={user.id}>
                      <td>
                        <strong>{user.username}</strong>
                        {self ? <div className="hint">{t('users.you')}</div> : null}
                      </td>
                      <td>{user.name}</td>
                      <td>
                        {roleLabel(user.role, t)}
                        <div className="hint">{user.role}</div>
                      </td>
                      <td>
                        <span className={user.disabled ? 'chip bad' : 'chip ok'}>
                          {user.disabled ? t('users.statusDisabled') : t('users.statusActive')}
                        </span>
                      </td>
                      <td>{dateTime(user.createdAt)}</td>
                      <td>
                        <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                          <button
                            type="button"
                            className="small"
                            disabled={working}
                            onClick={() => startEdit(user)}
                          >
                            {t('users.edit')}
                          </button>
                          <button
                            type="button"
                            className="small"
                            disabled={working}
                            title={t('users.revokeHint')}
                            onClick={() => void revoke(user)}
                          >
                            {working ? t('users.working') : t('users.revoke')}
                          </button>
                          <button
                            type="button"
                            className="small"
                            disabled={working}
                            onClick={() => void toggleBlock(user)}
                          >
                            {user.disabled ? t('users.unblock') : t('users.block')}
                          </button>
                          <button
                            type="button"
                            className="small danger"
                            disabled={working || self}
                            title={self ? t('users.selfDeleteTitle') : undefined}
                            onClick={() => void remove(user)}
                          >
                            {t('users.remove')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
        <p className="hint" style={{ marginTop: 12, marginBottom: 0 }}>
          {t('users.revokeHint')}
        </p>
      </Card>
    </div>
  );
}
